import Point from 'ol/geom/Point';

import { makeAPICall, makeMockAPICall } from 'models/api';
import { API_URL } from 'config';
import { BfWorkshopId, FaftWorkshopId } from 'models/workflow';
import { fromLonLat } from 'ol/proj';
import { WorkflowManager } from 'models';

export enum TypeEnum {
    'general',
    'add',
    'edit',
    'delete',
    'reply',
}

export enum CommentTypeDisplayEnum {
    general = 'GENERAL COMMENT',
    add = 'ADD INPUT',
    edit = 'EDIT INPUT',
    delete = 'DELETE INPUT',
    reply = 'REPLY',
}

export type Type = keyof typeof TypeEnum;

export const types = Object.keys(TypeEnum).filter((x) =>
    Number.isNaN(+x),
) as Type[];

export type CommentStatus = 'idle' | 'actioned' | 'rejected';

export declare namespace API {
    export interface Geometry {
        x: number;
        y: number;
    }

    export interface LiteUser {
        id: number;
        email: string;
        first_name: string;
        last_name: string;
    }

    export interface Comment {
        id: number;
        geometry: Geometry;
        type: Type;
        status: CommentStatus;
        bmc_id: number;
        plan_id: string;
        user_id: number;
        user: LiteUser;
        parent_id?: number | null;
        replies?: Comment[];
        workshop: BfWorkshopId | FaftWorkshopId;
        content: string;
        block_replies?: boolean;
        created: string;
        deleted: boolean;
        is_liked: boolean;
        total_likes: number;
        bmc_comment_id: number;
        bmc_parent_comment_id?: number | null;
    }

    export interface ActionResponse {
        success: boolean;
        error?: string;
    }
}

export interface LiteUser {
    id: number;
    email: string;
    fullName: string;
    firstName: string;
    lastName: string;
}

export interface Comment {
    id: number;
    geometry: Point;
    type: Type;
    status: CommentStatus;
    bmcId: number;
    planId: string;
    userId: number;
    user: LiteUser;
    parentId?: number | null;
    workshop: BfWorkshopId | FaftWorkshopId;
    content: string;
    created: Date;
    deleted: boolean;
    liked: boolean;
    likes: number;
    blockReplies?: boolean;
    bmcCommentId: number;
    bmcParentCommentId?: number | null;
}

export type NewComment = Omit<
    Comment,
    | 'id'
    | 'user'
    | 'userId'
    | 'created'
    | 'deleted'
    | 'liked'
    | 'likes'
    | 'bmcCommentId'
    | 'bmcParentCommentId'
>;

export type EditComment = Pick<
    Partial<Comment>,
    | 'type'
    | 'status'
    | 'blockReplies'
    | 'liked'
    | 'likes'
    | 'bmcCommentId'
    | 'bmcParentCommentId'
> &
    Pick<Comment, 'id'>;

const COMMENT_ROUTE = `${API_URL}/comments`;
const COMMENT_LIKES_ROUTE = `${API_URL}/comments-user-likes`;

const minute = 60 * 1000;
const day = minute * 60 * 24;

export const sampleTopLevelComments: API.Comment[] = [
    {
        id: 0,
        geometry: { x: 151.0716, y: -33.7921 },
        type: 'edit',
        status: 'rejected',
        bmc_id: 0,
        plan_id: '27f141e6-46f1-11ee-ac66-06b84d705550',
        user_id: 0,
        user: {
            id: 0,
            first_name: 'Michael',
            last_name: 'Bauer',
            email: 'michael.bauer@novasystems.com',
        },
        parent_id: null,
        workshop: 'bf_workshop_1',
        content:
            'I think this location should be protected at all costs! Increase priority!',
        block_replies: true,
        created: new Date(Date.now() - minute * 125 - day).toISOString(),
        deleted: false,
        is_liked: true,
        total_likes: 50,
        bmc_comment_id: 1,
        bmc_parent_comment_id: null,
    },
    {
        id: 1,
        geometry: { x: 151.0516, y: -33.7721 },
        type: 'add',
        status: 'actioned',
        bmc_id: 0,
        plan_id: '27f141e6-46f1-11ee-ac66-06b84d705550',
        user_id: 1,
        user: {
            id: 1,
            first_name: 'Joe',
            last_name: 'Random',
            email: 'joe.random@cfs.sa.gov.au',
        },
        parent_id: null,
        workshop: 'bf_workshop_1',
        content:
            'An X was recently built here and needs to be added to the map.',
        created: new Date(Date.now() - minute * 35).toISOString(),
        deleted: false,
        is_liked: true,
        total_likes: 50,
        bmc_comment_id: 1,
        bmc_parent_comment_id: null,
    },
    {
        id: 2,
        geometry: { x: 151.0416, y: -33.8021 },
        type: 'delete',
        status: 'idle',
        bmc_id: 0,
        plan_id: '27f141e6-46f1-11ee-ac66-06b84d705550',
        user_id: 1,
        user: {
            id: 1,
            first_name: 'Joe',
            last_name: 'Random',
            email: 'joe.random@cfs.sa.gov.au',
        },
        parent_id: null,
        workshop: 'bf_workshop_2',
        content:
            'I thought I read that X has moved away from here to another location. This should be removed',
        created: new Date(Date.now() - minute * 28).toISOString(),
        deleted: false,
        is_liked: false,
        total_likes: 150,
        bmc_comment_id: 1,
        bmc_parent_comment_id: null,
    },
];

export const sampleReplies: Record<number, API.Comment[]> = {
    0: [
        {
            id: 3,
            geometry: { x: 151.0716, y: -33.7921 },
            type: 'reply',
            status: 'idle',
            bmc_id: 0,
            plan_id: '27f141e6-46f1-11ee-ac66-06b84d705550',
            user_id: 2,
            user: {
                id: 2,
                first_name: 'Miss',
                last_name: 'Coordinator',
                email: 'miss.coordinator@cfs.sa.gov.au',
            },
            parent_id: 0,
            workshop: 'bf_workshop_1',
            content: 'No Michael, your old apartment is not critial to defend.',
            created: new Date(Date.now() - minute * 116 - day).toISOString(),
            deleted: false,
            is_liked: true,
            total_likes: 50,
            bmc_comment_id: 1,
            bmc_parent_comment_id: null,
        },
        {
            id: 4,
            geometry: { x: 151.0716, y: -33.7921 },
            type: 'reply',
            status: 'idle',
            bmc_id: 0,
            plan_id: '27f141e6-46f1-11ee-ac66-06b84d705550',
            user_id: 0,
            user: {
                id: 0,
                first_name: 'Michael',
                last_name: 'Bauer',
                email: 'michael.bauer@novasystems.com',
            },
            parent_id: null,
            workshop: 'bf_workshop_1',
            content: 'Pretty please!!!',
            created: new Date(Date.now() - minute * 65 - day).toISOString(),
            deleted: false,
            is_liked: true,
            total_likes: 50,
            bmc_comment_id: 1,
            bmc_parent_comment_id: null,
        },
        {
            id: 5,
            geometry: { x: 151.0716, y: -33.7921 },
            type: 'reply',
            status: 'idle',
            bmc_id: 0,
            plan_id: '27f141e6-46f1-11ee-ac66-06b84d705550',
            user_id: 2,
            user: {
                id: 2,
                first_name: 'Miss',
                last_name: 'Coordinator',
                email: 'miss.coordinator@cfs.sa.gov.au',
            },
            parent_id: 0,
            workshop: 'bf_workshop_1',
            content: 'No. Rejected.',
            created: new Date(Date.now() - minute * 58 - day).toISOString(),
            deleted: false,
            is_liked: true,
            total_likes: 50,
            bmc_comment_id: 1,
            bmc_parent_comment_id: 1,
        },
    ],
    1: [
        {
            id: 6,
            geometry: { x: 151.0516, y: -33.7721 },
            type: 'reply',
            status: 'idle',
            bmc_id: 0,
            plan_id: '27f141e6-46f1-11ee-ac66-06b84d705550',
            user_id: 2,
            user: {
                id: 2,
                first_name: 'Miss',
                last_name: 'Coordinator',
                email: 'miss.coordinator@cfs.sa.gov.au',
            },
            parent_id: 1,
            workshop: 'bf_workshop_1',
            content: 'Added.',
            created: new Date(Date.now() - minute * 5).toISOString(),
            deleted: false,
            is_liked: true,
            total_likes: 50,
            bmc_comment_id: 1,
            bmc_parent_comment_id: 2,
        },
    ],
    2: [
        {
            id: 7,
            geometry: { x: 151.0416, y: -33.8021 },
            type: 'reply',
            status: 'idle',
            bmc_id: 0,
            plan_id: '27f141e6-46f1-11ee-ac66-06b84d705550',
            user_id: 3,
            user: {
                id: 3,
                first_name: 'Random',
                last_name: 'User',
                email: 'random.user@cfs.sa.gov.au',
            },
            parent_id: 2,
            workshop: 'bf_workshop_2',
            content: 'Are you sure? I was there only a couple weeeks ago.',
            created: new Date(Date.now() - minute * 1).toISOString(),
            deleted: false,
            is_liked: true,
            total_likes: 50,
            bmc_comment_id: 1,
            bmc_parent_comment_id: 2,
        },
        {
            id: 8,
            geometry: { x: 151.0416, y: -33.8021 },
            type: 'reply',
            status: 'idle',
            bmc_id: 0,
            plan_id: '27f141e6-46f1-11ee-ac66-06b84d705550',
            user_id: 2,
            user: {
                id: 2,
                first_name: 'Miss',
                last_name: 'Coordinator',
                email: 'miss.coordinator@cfs.sa.gov.au',
            },
            parent_id: 2,
            workshop: 'bf_workshop_2',
            content:
                'Can someone confirm for me if this is still there or not?',
            created: new Date(Date.now() - minute * 0.1).toISOString(),
            deleted: false,
            is_liked: true,
            total_likes: 50,
            bmc_comment_id: 1,
            bmc_parent_comment_id: 0,
        },
    ],
};

export const mapAPIToComment = ({
    geometry,
    bmc_id,
    plan_id,
    user_id,
    user,
    parent_id,
    block_replies,
    created,
    is_liked,
    total_likes,
    bmc_comment_id,
    bmc_parent_comment_id,
    ...rest
}: API.Comment): Comment => ({
    ...rest,
    geometry: new Point(fromLonLat([geometry.x, geometry.y])),
    bmcId: bmc_id,
    planId: plan_id,
    userId: user_id,
    user: {
        id: user.id,
        email: user.email,
        firstName: user.first_name,
        lastName: user.last_name,
        fullName: `${user.first_name} ${user.last_name}`,
    },
    created: new Date(created),
    parentId: parent_id,
    blockReplies: block_replies,
    likes: total_likes,
    liked: is_liked,
    bmcCommentId: bmc_comment_id,
    bmcParentCommentId: bmc_parent_comment_id,
});

export const sortByDate = (a: Comment, b: Comment) =>
    a.created > b.created ? 1 : -1;

export const getComments = makeAPICall<Comment[], void, API.Comment[]>(
    (payload, state) => {
        const params = new URLSearchParams();
        params.append('limit', '1000');
        if (state) {
            const bfmc = state.guide.selectedBFMC;
            const selPlans = bfmc?.plans.filter(
                (plan) => plan.plan_type === state.guide.selectedPlanType,
            );
            const currentPlan =
                selPlans && selPlans?.length > 0 ? selPlans[0] : null;

            if (bfmc) {
                const workflowNotStarted =
                    state.guide.selectedPlanType === 'bfrmp'
                        ? 'bf_not_started'
                        : 'faft_not_started';
                const workflow = currentPlan
                    ? WorkflowManager.getWorkflow(
                          state.guide.selectedPlanType,
                          currentPlan.workshop_status,
                      )
                    : WorkflowManager.getWorkflow(
                          state.guide.selectedPlanType,
                          workflowNotStarted,
                      );
                const group = state.guide.BFMCGroups.object?.find(
                    (g) => bfmc && g.bmcs.includes(bfmc.id),
                );

                // handle the group scenario
                if (
                    workflow.scope === 'group' &&
                    group &&
                    state.guide.BFMCs.object
                ) {
                    group.bmcs.forEach((bmcId) => {
                        const currBfmc = state.guide.BFMCs.object?.find(
                            (theBfmc) => theBfmc.id === bmcId,
                        );
                        if (currBfmc) {
                            const selectedPlans = currBfmc.plans.filter(
                                (plan) =>
                                    plan.plan_type ===
                                    state.guide.selectedPlanType,
                            );
                            if (selectedPlans.length > 0) {
                                const currPlan = selectedPlans[0];
                                params.append('plan_ids', `${currPlan.id}`);
                            }
                        }
                    });
                } else if (workflow.scope === 'individual' && currentPlan) {
                    params.append('plan_ids', `${currentPlan.id}`);
                }
            }
        }

        return {
            url: `${COMMENT_ROUTE}/?${params.toString()}`,
        };
    },
    (raw) => raw.map(mapAPIToComment).sort(sortByDate),
);

export const getReplies = makeAPICall<
    Comment[],
    { parent: number },
    API.Comment
>(
    ({ parent }) => ({
        url: `${COMMENT_ROUTE}/${parent}`,
    }),
    (raw) => raw.replies?.map(mapAPIToComment).sort(sortByDate) ?? [],
);

export const addComment = makeAPICall<
    API.ActionResponse,
    { comment: NewComment },
    API.ActionResponse
>(
    ({ comment }) => {
        const data = new FormData();

        const {
            bmcId,
            planId,
            type,
            status,
            content,
            workshop,
            geometry,
            parentId,
            blockReplies,
        } = comment;

        data.append('bmc_id', `${bmcId}`);
        data.append('plan_id', `${planId}`);
        data.append('type', type);
        data.append('status', status);
        data.append('content', content);
        data.append('block_replies', `${blockReplies ?? false}`);
        if (workshop) data.append('workshop', workshop);
        if (geometry) {
            const coords = geometry.getCoordinates();
            data.append('location_x', `${coords[0]}`);
            data.append('location_y', `${coords[1]}`);
        }
        if (parentId) data.append('parent_id', `${parentId}`);

        return {
            url: `${COMMENT_ROUTE}/`,
            contentType: null,
            requestData: {
                method: 'POST',
                body: data,
            },
        };
    },
    (x) => x,
);

export const updateComment = makeAPICall<
    API.ActionResponse,
    { edits: EditComment },
    API.ActionResponse
>(
    ({ edits }) => {
        const data = new FormData();

        const { id, type, status, blockReplies } = edits;

        if (type) data.append('type', type);
        if (status) data.append('status', status);
        if (blockReplies != null)
            data.append('block_replies', `${blockReplies}`);

        return {
            url: `${COMMENT_ROUTE}/${id}`,
            contentType: null,
            requestData: {
                method: 'PUT',
                body: data,
            },
        };
    },
    (x) => x,
);

export const deleteComment = makeAPICall<
    API.ActionResponse,
    { id: Comment['id'] },
    API.ActionResponse
>(
    ({ id }) => ({
        url: `${COMMENT_ROUTE}/${id}`,
        contentType: null,
        requestData: {
            method: 'DELETE',
        },
    }),
    (x) => x,
);

export const likeComment = makeAPICall<
    API.ActionResponse,
    { id: Comment['id'] },
    API.ActionResponse
>(
    ({ id }) => {
        const data = new FormData();
        data.append('comment_id', `${id}`);
        return {
            url: `${COMMENT_LIKES_ROUTE}/`,
            contentType: null,
            requestData: {
                method: 'POST',
                body: data,
            },
        };
    },
    (x) => x,
);

export const unlikeComment = makeAPICall<
    API.ActionResponse,
    { id: Comment['id'] },
    API.ActionResponse
>(
    ({ id }) => ({
        url: `${COMMENT_LIKES_ROUTE}/${id}`,
        contentType: null,
        requestData: {
            method: 'DELETE',
        },
    }),
    (x) => x,
);

export const addMockComment = makeMockAPICall<
    API.ActionResponse,
    { comment: NewComment },
    API.ActionResponse
>(
    () => ({
        url: COMMENT_ROUTE,
    }),
    (raw, { comment }, state) => {
        // Fake adding the comment
        if (state?.auth?.user?.object) {
            const { firstName, lastName, email, id } = state.auth.user.object;
            const { bmcId, planId, parentId, blockReplies, geometry, ...rest } =
                comment;

            const newComment: API.Comment = {
                ...rest,
                id: Math.round(Math.random() * 10000),
                user_id: id,
                user: {
                    id,
                    email,
                    first_name: firstName,
                    last_name: lastName,
                },
                bmc_id: bmcId,
                plan_id: planId,
                parent_id: parentId,
                block_replies: blockReplies,
                geometry: {
                    x: geometry.getCoordinates()[0],
                    y: geometry.getCoordinates()[1],
                },
                created: new Date().toISOString(),
                deleted: false,
                is_liked: false,
                total_likes: 0,
                bmc_comment_id: 1,
                bmc_parent_comment_id: 0,
            };
            if (parentId) {
                if (!sampleReplies[parentId]) sampleReplies[parentId] = [];
                sampleReplies[parentId].push(newComment);
            } else {
                sampleTopLevelComments.push(newComment);
            }
        }
        return raw;
    },
    { success: true },
    500,
);

export const exportData = makeAPICall<
    ArrayBuffer,
    {
        queryParams: string;
        apiEndPoint: string;
        contentType: string;
    },
    ArrayBuffer
>(
    ({ queryParams, apiEndPoint, contentType }) => {
        return {
            url: `${COMMENT_ROUTE}/${apiEndPoint}?${queryParams}`,
            dataType: 'blob',
            contentType: contentType,
        };
    },
    (data) => {
        return data;
    },
);
